import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import pointInPolygon from 'point-in-polygon';
import { setCmmeStates, setFictiveSections } from '@/actions';
import { ZOOM } from '@/constants';
import { createClassName } from '@/helpers';
import CornerRenderer from '../../shared/CornerRenderer';

const PageMatrixCornerRenderer = props => {
  const { page, fictiveSections, fictiveSectionsSelection, corner, matrixSelectionArea, parentShield, selectionArea, setSelectionArea, sectionsSelection } = props;
  const dispatch = useDispatch();
  const cmmeStates = useSelector(state => state.core.cmmeStates);
  const scaleCoef = useSelector(state => state.core.scaleCoef);
  const [isHidden, setIsHidden] = useState(null);
  const { x, y, width = 10, height = 10, id } = corner;

  const onDrag = ({ dx, dy }) => {
    setIsHidden(true);
    if (matrixSelectionArea?.corners?.length) {
      const newX = Number((corner.x + dx / scaleCoef / ZOOM).toFixed(8));
      const newY = Number((corner.y + dy / scaleCoef / ZOOM).toFixed(8));

      matrixSelectionArea.corners.forEach((c, index) => {
        if (c.id === corner.id) {
          c.x = newX;
          c.y = newY;

          const prev = index > 0 ? index - 1 : matrixSelectionArea.corners.length - 1;
          const next = index < matrixSelectionArea.corners.length - 1 ? index + 1 : 0;
          const prevC = matrixSelectionArea.corners[prev];
          const nextC = matrixSelectionArea.corners[next];

          if (prevC.x === corner.x && prevC.y !== corner.y) {
            prevC.x = newX;
          } else if (prevC.x !== corner.x && prevC.y === corner.y) {
            prevC.y = newY;
          }
          if (nextC.x === corner.x && nextC.y !== corner.y) {
            nextC.x = newX;
          } else if (nextC.x !== corner.x && nextC.y === corner.y) {
            nextC.y = newY;
          }
        }
      });
      setSelectionArea(matrixSelectionArea);
    }
  };

  const onDragEnd = ({ dx, dy }) => {
    if (selectionArea) {
      Object.values(sectionsSelection).forEach(section => {
        delete sectionsSelection[section.id];
      });
      Object.values(fictiveSectionsSelection).forEach(section => {
        delete fictiveSectionsSelection[section.id];
      });
      const newX = Number((corner.x + dx / scaleCoef / ZOOM).toFixed(8));
      const newY = Number((corner.y + dy / scaleCoef / ZOOM).toFixed(8));

      selectionArea.corners.forEach((c, index) => {
        if (c.id === corner.id) {
          c.x = newX;
          c.y = newY;
          const prev = index > 0 ? index - 1 : matrixSelectionArea.corners.length - 1;
          const next = index < matrixSelectionArea.corners.length - 1 ? index + 1 : 0;
          const prevC = matrixSelectionArea.corners[prev];
          const nextC = matrixSelectionArea.corners[next];
          if (prevC.x === corner.x && prevC.y !== corner.y) {
            prevC.x = newX;
          } else if (prevC.x !== corner.x && prevC.y === corner.y) {
            prevC.y = newY;
          }
          if (nextC.x === corner.x && nextC.y !== corner.y) {
            nextC.x = newX;
          } else if (nextC.x !== corner.x && nextC.y === corner.y) {
            nextC.y = newY;
          }
          return;
        }
      });
      let polygon = [];
      if (selectionArea?.corners.length > 0) {
        selectionArea.corners.forEach(corner => {
          polygon.push([corner.x, corner.y]);
        });
        setSelectionArea(null);
        setIsHidden(null);
      }

      if (page?.sections && polygon.length > 0) {
        Object.values(fictiveSections).forEach(section => {
          const stepsX = 10;
          const stepsY = 10;
          const stepX = Number(((section.width - 0.2) / stepsX).toFixed(10));
          const stepY = Number(((section.height - 0.2) / stepsY).toFixed(10));
          if (sectionsSelection[section.id]) delete sectionsSelection[section.id];
          if (fictiveSectionsSelection[section.id]) delete fictiveSectionsSelection[section.id];

          if (section.type !== 'separator' && stepsX > 0 && stepsY > 0) {
            for (let x = 0; x < stepsX; x++) {
              for (let y = 0; y < stepsY; y++) {
                const point = [section.x + 0.1 + x * stepX, section.y + 0.1 + y * stepY];
                section.isSelected = false;
                if (pointInPolygon(point, polygon)) {
                  if (section.type === 'helpSec') {
                    section.isSelected = true;
                    fictiveSections[section.id] = section;
                    fictiveSectionsSelection[section.id] = section;
                  } else {
                    sectionsSelection[section.id] = section;
                  }
                  y = stepsY;
                  x = stepsX;
                  break;
                }
              }
            }
          }
        });

        setTimeout(() => {
          const fictiveSectionByShield = Object.fromEntries(Object.entries(fictiveSections).filter(([key]) => key.match(parentShield.id)));

          const sectionsSelectionByShield = Object.fromEntries(Object.entries(sectionsSelection).filter(([key]) => key.match(parentShield.id)));

          const fictiveSelectionByShield = Object.fromEntries(Object.entries(fictiveSectionsSelection).filter(([key]) => key.match(parentShield.id)));

          dispatch(
            setFictiveSections({
              page: page,
              fictiveSections: fictiveSectionByShield,
              sectionsSelection: sectionsSelectionByShield,
              fictiveSectionsSelection: fictiveSelectionByShield,
            }),
          );

          if (cmmeStates?.pages && cmmeStates.pages[page.uid]) {
            if (sectionsSelectionByShield && Object.keys(sectionsSelectionByShield)?.length > 1) {
              cmmeStates.pages[page.uid].sectionsSelection = sectionsSelectionByShield;
              cmmeStates.pages[page.uid].selectedSection = undefined;
            } else {
              cmmeStates.pages[page.uid].sectionsSelection = {};
              cmmeStates.pages[page.uid].selectedSection = Object.keys(sectionsSelectionByShield)[0];
            }
            dispatch(setCmmeStates(cmmeStates));
          }
        }, 10);
      }
    }
  };

  const clickable = true;
  const draggable = corner.edge?.length > 0;
  const selectedSectionKeys = Object.keys(sectionsSelection);
  const selectedSections = Object.values(page.sections).filter(section => selectedSectionKeys.includes(section.id));
  const className = createClassName({ clickable, draggable });

  return (
    <CornerRenderer
      key={`${corner.id}-corner-component`}
      id={corner.id}
      className={className}
      selectedSections={selectedSections}
      scaleCoef={scaleCoef}
      rectProps={{ x: x, y: y, width: draggable ? width : 2, height: draggable ? height : 2 }}
      dx={x}
      dy={y}
      isHidden={isHidden}
      isSelected={true}
      clickable={clickable}
      draggable={draggable}
      dragTransforms={page.transforms}
      resizeType={page.resizeType}
      resizable={false}
      corner={corner}
      onDragEnd={onDragEnd}
      onDrag={onDrag}
      page={page}
      shield={parentShield}
      quantity={matrixSelectionArea.corners?.length}
    />
  );
};

export default React.memo(PageMatrixCornerRenderer);
